import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { withGuard } from 'ui-components/components/with-guard';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import messages from './messages';
import styles from './styles.module.less';
import GetFulfillmentProviderDisplayName from '../../../../../components/data-driven-logo/get-fulfillment-provider-display-name';
import ModalPortal from '../../../../../components/modal-portal';
import { useEnableDirectModeCarriers } from '../../../../../feature-flags/hooks/use-enable-direct-mode-carriers';
import { DeleteFulfillmentProvider } from '../../../../../connection-components/delete-fulfillment-provider';
import { sellerProvidersUpdated } from '../../../../../store/seller/seller.actions';
import { useProvidersGridContext } from '../../providers-grid-context';
import { ProviderPropType } from '../../types';

const useProviderDisplayName = (provider) => {
  const { formatMessage } = useIntl();
  const nameMessage = GetFulfillmentProviderDisplayName(provider.fillProviderId);
  return formatMessage(nameMessage, nameMessage.values);
};

const DeleteFulfillmentProviderModal = ({ onClose, onError, provider }) => {
  const isDirectMode = useEnableDirectModeCarriers();
  const dispatch = useDispatch();

  const { fulfillmentProviders } = useProvidersGridContext();

  const name = useProviderDisplayName(provider);

  const closeModalWithError = (error) => {
    onClose();
    onError(error);
  };

  const onRemoveAccount = () => {
    dispatch(sellerProvidersUpdated());
    fulfillmentProviders.updateList();
    onClose();
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.successNotification} />,
      content: <FormattedMessage {...messages.accountRemovedSuccessfully} values={{ name }} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      duration: 3000,
    });
  };

  return (
    <ModalPortal containerId='delete-modal' onClose={onClose}>
      <Modal className={classnames(styles['delete-modal'])}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...messages.deleteProvider} values={{ name: provider.partnerName }} />
        </ModalHeader>
        <ModalBody className={classnames(styles['modal-body'], { [styles['iframe-mode']]: !isDirectMode })}>
          <DeleteFulfillmentProvider
            onCancel={onClose}
            onError={closeModalWithError}
            onRemoveAccount={onRemoveAccount}
            sellerFulfillmentProviderId={provider.sellerFillProviderId.toString()}
          />
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};

DeleteFulfillmentProviderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  provider: ProviderPropType.isRequired,
};

const EnhancedModal = withGuard(DeleteFulfillmentProviderModal);
export { EnhancedModal as DeleteFulfillmentProviderModal };
